import React, { useRef, useState, useEffect } from 'react';
import styles from './Dropdown.module.scss';
import classNames from 'classnames';

interface DropdownOption {
	value: string;
	label: string;
}

interface DropdownProps {
	label?: string;
	options: DropdownOption[];
	value: string | null;
	onChange: (value: string) => void;
	placeholder?: string;
	id?: string;
}

export const UI_Dropdown: React.FC<DropdownProps> = ({ label, options, value, onChange, placeholder = '', id }) => {
	const [open, setOpen] = useState(false);
	const [focusedIndex, setFocusedIndex] = useState<number>(-1);
	const selectRef = useRef<HTMLDivElement>(null);
	const listRef = useRef<HTMLUListElement>(null);

	const selectedOption = options.find((opt) => opt.value === value);

	useEffect(() => {
		const handleClick = (e: MouseEvent) => {
			if (selectRef.current && !selectRef.current.contains(e.target as Node)) {
				setOpen(false);
				setFocusedIndex(-1);
			}
		};
		if (open) document.addEventListener('mousedown', handleClick);
		return () => document.removeEventListener('mousedown', handleClick);
	}, [open]);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			if (open && focusedIndex >= 0) {
				onChange(options[focusedIndex].value);
				setOpen(false);
				setFocusedIndex(-1);
			} else {
				setOpen((prev) => !prev);
				if (!open) setFocusedIndex(0);
			}
		} else if (e.key === 'Escape') {
			setOpen(false);
			setFocusedIndex(-1);
		} else if (e.key === 'ArrowDown') {
			e.preventDefault();
			if (!open) {
				setOpen(true);
				setFocusedIndex(0);
			} else {
				setFocusedIndex((prev) => (prev < options.length - 1 ? prev + 1 : 0));
			}
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			if (!open) {
				setOpen(true);
				setFocusedIndex(options.length - 1);
			} else {
				setFocusedIndex((prev) => (prev > 0 ? prev - 1 : options.length - 1));
			}
		}
	};

	useEffect(() => {
		if (open && focusedIndex >= 0 && listRef.current) {
			const btn = listRef.current.querySelectorAll('button')[focusedIndex];
			btn?.focus();
		}
	}, [focusedIndex, open]);

	const handleOptionClick = (option: DropdownOption) => {
		onChange(option.value);
		setOpen(false);
		setFocusedIndex(-1);
	};

	return (
		<div className={styles.Dropdown}>
			{label && (
				<div id={id ? `${id}-label` : undefined} className={styles.Dropdown_label}>
					{label}
				</div>
			)}
			<div
				className={classNames(styles.Dropdown_customSelect, open && styles.open)}
				ref={selectRef}
				tabIndex={0}
				role="combobox"
				aria-haspopup="listbox"
				aria-expanded={open}
				aria-controls={id ? `${id}-listbox` : undefined}
				aria-labelledby={id ? `${id}-label` : undefined}
				onClick={() => setOpen((prev) => !prev)}
				onKeyDown={handleKeyDown}
				id={id}
			>
				<div className={styles.Dropdown_selectedOption} aria-live="polite" aria-atomic="true" id={id ? `${id}-selectedOption` : undefined}>
					{selectedOption ? selectedOption.label : placeholder}
				</div>
				<ul
					className={styles.Dropdown_optionsList}
					role="listbox"
					aria-labelledby={id ? `${id}-label` : undefined}
					style={{ display: open ? 'block' : 'none' }}
					ref={listRef}
				>
					{options.map((option, idx) => (
						<li key={option.value} className={styles.Dropdown_option}>
							<button
								className={classNames(
									styles.Dropdown_btn,
									value === option.value && styles.selected,
									focusedIndex === idx && styles.focused,
								)}
								role="option"
								aria-selected={value === option.value}
								tabIndex={-1}
								onClick={(e) => {
									e.stopPropagation();
									handleOptionClick(option);
								}}
								onFocus={() => setFocusedIndex(idx)}
							>
								{option.label}
							</button>
						</li>
					))}
				</ul>
			</div>
		</div>
	);
};
